import Dexie, { type Table } from 'dexie'

/**
 * Local-first store.
 *
 * OVAL's sites sit on 2G or no connectivity at all, so the browser holds a real
 * copy of everything the site needs and every mutation is queued locally before
 * it ever touches the network.
 *
 *   `cache`  — server rows, keyed by "<collection>:<id>". Read path.
 *   `outbox` — mutations waiting for a connection. Write path.
 *   `meta`   — sync bookkeeping.
 */

export interface CacheRow {
  key: string          // "materials:MAT-001"
  collection: string   // "materials"
  id: string
  site_id: string | null
  data: unknown
  cached_at: number
}

export type OutboxStatus = 'pending' | 'sending' | 'failed' | 'done'

export interface OutboxEntry {
  id?: number
  client_txn_id: string       // idempotency key, replayed safely after a dropped 2G call
  kind: OutboxKind
  label: string               // human sentence shown in the sync tray
  payload: unknown
  doc_type: string
  local_doc_id: string | null // optimistic id shown until the server assigns the real one
  server_doc_id: string | null
  status: OutboxStatus
  attempts: number
  last_error: string | null
  created_at: number
  updated_at: number
}

export type OutboxKind =
  | 'indent.create'
  | 'indent.update'
  | 'pr.create'
  | 'pr.submit'
  | 'pr.action'
  | 'ist_pr.create'
  | 'ist.create'
  | 'ist.status'
  | 'po.create'
  | 'po.submit'
  | 'po.action'
  | 'po.despatch'
  | 'po.ack'
  | 'grn.create'
  | 'grn.action'
  | 'min.create'
  | 'min.action'
  | 'mrn.create'
  | 'mrn.action'
  | 'adjustment.create'
  | 'asset.create'
  | 'attachment.upload'
  // Corrections to a document the approver sent back. Header + lines are
  // rewritten wholesale; the document keeps its number and its audit trail.
  | 'doc.revise'

export interface MetaRow {
  key: string
  value: unknown
}

class OvalDexie extends Dexie {
  cache!: Table<CacheRow, string>
  outbox!: Table<OutboxEntry, number>
  meta!: Table<MetaRow, string>

  constructor() {
    super('oval-ims')
    this.version(1).stores({
      cache: 'key, collection, id, site_id, cached_at, [collection+site_id]',
      outbox: '++id, client_txn_id, status, kind, created_at, doc_type',
      meta: 'key',
    })
  }
}

export const localDb = new OvalDexie()

// ---------------------------------------------------------------------------
// Cache helpers
// ---------------------------------------------------------------------------
export async function putCache(collection: string, rows: unknown[], idField = 'id') {
  if (!rows?.length) return
  const now = Date.now()
  const entries: CacheRow[] = rows.map((r) => {
    // deno-lint-ignore no-explicit-any
    const row = r as any
    const id = String(row[idField])
    return {
      key: `${collection}:${id}`,
      collection,
      id,
      site_id: row.site_id ?? null,
      data: row,
      cached_at: now,
    }
  })
  await localDb.cache.bulkPut(entries)
  await localDb.meta.put({ key: `synced:${collection}`, value: now })
}

export async function readCache<T>(collection: string, siteId?: string | null): Promise<T[]> {
  const rows = siteId
    ? await localDb.cache.where('[collection+site_id]').equals([collection, siteId]).toArray()
    : await localDb.cache.where('collection').equals(collection).toArray()
  return rows.map((r) => r.data as T)
}

export async function readCacheOne<T>(collection: string, id: string): Promise<T | undefined> {
  const row = await localDb.cache.get(`${collection}:${id}`)
  return row?.data as T | undefined
}

export async function cacheAgeMs(collection: string): Promise<number | null> {
  const m = await localDb.meta.get(`synced:${collection}`)
  return m ? Date.now() - (m.value as number) : null
}

export async function clearLocalData() {
  await Promise.all([localDb.cache.clear(), localDb.outbox.clear(), localDb.meta.clear()])
}
